/**
 * Single source of truth for derived salon facts used by comparison tables,
 * transport blocks and rating blocks.
 *
 * Any page that shows "antal barberare", "betyg" or "öppettider" MUST read
 * them from here so the numbers can never drift apart between blocks.
 */
import { salonBySlug, salons } from "@/data/salons";
import { SALON_BARBERARE } from "@/data/barbers";
import type { OpeningHour, Salon, SalonSlug } from "@/data/types";
import {
  bokadirektCountFor,
  bokadirektRatingFor,
  formatCount,
  formatRating,
  ratingFor,
  reviewCountFor,
} from "@/data/reviewStats";

const WEEKDAYS: OpeningHour["day"][] = ["Mån", "Tis", "Ons", "Tor", "Fre"];

function short(hours: string): string {
  return hours.replace(/:00/g, "").replace(/\s/g, "");
}

function hoursFor(salon: Salon, day: OpeningHour["day"]): string | null {
  return salon.openingHours.find((h) => h.day === day)?.hours ?? null;
}

/** "10–20" when all weekdays match, otherwise "Mån–Tor 10–19, Fre 10–18". */
export function weekdayHours(salon: Salon): string {
  const values = WEEKDAYS.map((d) => hoursFor(salon, d));
  const first = values[0];
  if (first && values.every((v) => v === first)) return short(first);
  return WEEKDAYS.map((d, i) => `${d} ${values[i] ? short(values[i]!) : "stängt"}`).join(", ");
}

/** "10–18" when Lör = Sön, otherwise "Lör 10–19, Sön 11–19". */
export function weekendHours(salon: Salon): string {
  const sat = hoursFor(salon, "Lör");
  const sun = hoursFor(salon, "Sön");
  if (sat && sat === sun) return short(sat);
  return [sat ? `Lör ${short(sat)}` : "Lör stängt", sun ? `Sön ${short(sun)}` : "Sön stängt"].join(", ");
}

/** Two source-specific rating lines. */
export function ratingText(salon: Salon): string {
  return `${formatRating(ratingFor(salon.slug))} av 5 på Google (${formatCount(reviewCountFor(salon.slug))} omdömen)\n${formatRating(bokadirektRatingFor(salon.slug))} av 5 på BokaDirekt (${formatCount(bokadirektCountFor(salon.slug))} omdömen)`;
}

/** Number of barbers listed for a salon in @/data/barbers. */
export function barberCount(slug: SalonSlug): number {
  return SALON_BARBERARE[slug as keyof typeof SALON_BARBERARE]?.barbers.length ?? 0;
}

export interface SalonStats {
  key: SalonSlug;
  salon: Salon;
  barbers: number;
  ratingText: string;
  vardagar: string;
  helg: string;
}

export function salonStats(slug: SalonSlug): SalonStats {
  const salon = salonBySlug[slug];
  return {
    key: slug,
    salon,
    barbers: barberCount(slug),
    ratingText: ratingText(salon),
    vardagar: weekdayHours(salon),
    helg: weekendHours(salon),
  };
}

export function salonStatsFor(order: readonly SalonSlug[]): SalonStats[] {
  return order.map(salonStats);
}

/**
 * Consistency check shared by the build-time validator (scripts/validateData.ts)
 * and dev-time runtime assertions. Returns a list of human readable problems.
 */
export function checkSalonDataConsistency(
  localInfo?: Partial<Record<SalonSlug, { lage: string }>>,
): string[] {
  const problems: string[] = [];

  for (const salon of salons) {
    if (!(salon.slug in SALON_BARBERARE)) {
      problems.push(`salon:${salon.slug} saknar barberarlista i SALON_BARBERARE`);
    } else if (barberCount(salon.slug) === 0) {
      problems.push(`salon:${salon.slug} har 0 barberare i SALON_BARBERARE`);
    }
    if (!(salon.rating >= 1 && salon.rating <= 5)) {
      problems.push(`salon:${salon.slug} rating utanför 1–5 (${salon.rating})`);
    }
    if (!Number.isFinite(salon.reviewCount) || salon.reviewCount < 0) {
      problems.push(`salon:${salon.slug} ogiltigt reviewCount (${salon.reviewCount})`);
    }
    for (const day of [...WEEKDAYS, "Lör", "Sön"] as OpeningHour["day"][]) {
      if (!hoursFor(salon, day)) {
        problems.push(`salon:${salon.slug} saknar öppettid för ${day}`);
      }
    }
  }

  for (const key of Object.keys(SALON_BARBERARE) as SalonSlug[]) {
    if (!salonBySlug[key]) {
      problems.push(`SALON_BARBERARE["${key}"] pekar på en salong som inte finns`);
    }
  }

  if (localInfo) {
    for (const [key, info] of Object.entries(localInfo) as [SalonSlug, { lage: string }][]) {
      const salon = salonBySlug[key];
      if (!salon) {
        problems.push(`sidlokal info för okänd salong "${key}"`);
      } else if (!info.lage.startsWith(salon.address)) {
        problems.push(
          `sidlokal "lage" för ${key} ("${info.lage}") matchar inte adressen i @/data/salons ("${salon.address}")`,
        );
      }
    }
  }

  return problems;
}

/** Throws in dev, no-ops in production builds. */
export function assertSalonDataConsistency(
  localInfo?: Partial<Record<SalonSlug, { lage: string }>>,
): void {
  if (import.meta.env?.PROD) return;
  const problems = checkSalonDataConsistency(localInfo);
  if (problems.length) {
    throw new Error(`Datakonsistensfel i salongsdata:\n- ${problems.join("\n- ")}`);
  }
}
